import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Grid, Paper, Typography, CircularProgress, TextField, MenuItem, Checkbox,
  Pagination, InputAdornment, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, IconButton, Tooltip,
} from '@mui/material';
import ViewListIcon from '@mui/icons-material/ViewList';
import GridViewIcon from '@mui/icons-material/GridView';
import ApiService from "../../functions/apiService";
import { Col, Row } from 'react-bootstrap';
import ButtonTheme from '../../components/ThemeButton/ThemeButton';
import { toast } from 'react-toastify';
import './certificates.css'
import AddCerificate from './AddCertificate';
import Modal from 'react-bootstrap/Modal';
import ViewCertificate from './ViewCertificate';
import SearchIcon from "@mui/icons-material/Search";
import { Link } from "react-router-dom";

// ─── Helpers ────────────────────────────────────────────────────────────────

const ExpiryBadge = ({ count, type = 'neutral' }) => {
  let cls = 'expiry-badge neutral';
  if (count > 0) cls = type === 'expired' ? 'expiry-badge red' : 'expiry-badge amber';
  return <span className={cls}>{count}</span>;
};

const getUrgencyStyle = (cert) => {
  if (cert.expired_count > 0) return { borderLeft: '4px solid #FF0000' };
  if (cert.expiring_30_days_count > 0) return { borderLeft: '4px solid #F8BD00' };
  return { borderLeft: '4px solid transparent' };
};

// ─── Component ──────────────────────────────────────────────────────────────

const Certificates = () => {
  const [addModal, setAddModal] = useState(false);
  const [certificates, setCertificates] = useState([]);
  const [deletModal, setDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState('');
  const [viewModal, setViewModal] = useState(false);
  const [viewId, setViewId] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const currentPageRef = useRef(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchResult, setSearchResult] = useState('');
  const itemsPerPage = 6;
  const [viewMode, setViewMode] = useState('grid');
  const [summary, setSummary] = useState({ total: 0, expired: 0, expiringSoon: 0, expiringYear: 0 });

  // ── Data loading ──────────────────────────────────────────────────────────

  const loadCertificates = (showLoader = false) => {
    if (showLoader) setLoading(true);
    ApiService.get(`/get-certificates?page=${currentPageRef.current}&limit=${itemsPerPage}&search=${searchResult}`)
      .then(({ data }) => {
        setCertificates(data?.data?.data || []);
        setTotalPages(Math.ceil(data.data.total / itemsPerPage));
        setTotalCount(data.data.total || 0);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  const loadSummary = () => {
    ApiService.get('/get-certificates?page=1&limit=9999&search=')
      .then(({ data }) => {
        const all = data?.data?.data || [];
        setSummary({
          total: data.data.total || 0,
          expired: all.reduce((s, c) => s + (c.expired_count > 0 ? 1 : 0), 0),
          expiringSoon: all.reduce((s, c) => s + (c.expiring_30_days_count > 0 ? 1 : 0), 0),
          expiringYear: all.reduce((s, c) => s + (c.expiring_this_year_count > 0 ? 1 : 0), 0),
        });
      })
      .catch(() => {});
  };

  const deleteCertificate = () => {
    setLoading(true);
    ApiService.get('/delete-certificate/' + deleteId)
      .then(({ data }) => {
        setDeleteId('');
        setDeleteModal(false);
        toast.success(data.message);
        loadCertificates();
        loadSummary();
        setLoading(false);
      })
      .catch((err) => toast.error(err?.response?.data?.message));
  };

  useEffect(() => {
    currentPageRef.current = currentPage;
    loadCertificates(!searchResult);
  }, [currentPage, searchResult]);

  useEffect(() => {
    loadSummary();
  }, []);

  const handlePageChange = (_, value) => setCurrentPage(value);

  const clearFilters = () => {
    setSearchResult('');
    setCurrentPage(1);
  };

  const hasActiveFilters = !!searchResult;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      {loading ? (
        <Paper className="w-100 h-100 d-flex justify-content-center align-items-center">
          <CircularProgress />
        </Paper>
      ) : (
        <Paper className="p-4" style={{ marginTop: "1rem" }}>

          {/* ── Header row ── */}
          <Grid container spacing={2} alignItems="center" justifyContent="space-between">
            <Grid item xs={9}>
              <Typography variant="h5">Certification Module</Typography>
            </Grid>
            <Grid item xs={3}>
              <TextField
                className='w-100'
                placeholder='Search'
                variant="outlined"
                value={searchResult}
                onChange={(e) => setSearchResult(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>

          {/* ── Stats bar ── */}
          {summary.total > 0 && (
            <div className="cert-stats-bar">
              <div className={`cert-stat-item${!searchResult ? ' active' : ''}`} onClick={clearFilters}>
                <div className="cert-stat-value">{summary.total}</div>
                <div className="cert-stat-label">Total Certificates</div>
              </div>
              <div className="cert-stat-item cert-stat-item--red">
                <div className="cert-stat-value" style={{ color: summary.expired > 0 ? '#cc0000' : '#555555' }}>{summary.expired}</div>
                <div className="cert-stat-label">With Expired Members</div>
              </div>
              <div className="cert-stat-item cert-stat-item--amber">
                <div className="cert-stat-value" style={{ color: summary.expiringSoon > 0 ? '#b36b00' : '#555555' }}>{summary.expiringSoon}</div>
                <div className="cert-stat-label">Expiring Within 30 Days</div>
              </div>
              <div className="cert-stat-item">
                <div className="cert-stat-value">{summary.expiringYear}</div>
                <div className="cert-stat-label">Expiring This Year</div>
              </div>
            </div>
          )}

          {/* ── Empty state ── */}
          {!certificates || certificates.length === 0 ? (
            hasActiveFilters ? (
              <Box>
                <div className='d-flex flex-column align-items-center justify-content-center' style={{ height: 400 }}>
                  <img src='/images/blur-logo.png' style={{ height: 200, width: 250 }} alt="" />
                  <span className='mt-4' style={{ color: '#555555' }}>No certificates match your filters.</span>
                  <ButtonTheme variant="filled" color='primary' className='mt-3' onClick={clearFilters}>
                    Clear Filters
                  </ButtonTheme>
                </div>
              </Box>
            ) : (
              <Box>
                <div className='mt-4' style={{ color: '#555555' }}>
                  There are no certificates available, please add them as needed.
                </div>
                <div className='inner-box'>
                  <img src='/images/blur-logo.png' alt="" />
                  <ButtonTheme type='submit' variant="filled" color='primary' className='mt-4' onClick={() => setAddModal(true)}>
                    Create New Certificate
                  </ButtonTheme>
                </div>
              </Box>
            )
          ) : (
            <div>
              {/* ── List heading row ── */}
              <div className='mt-4 d-flex align-items-center' style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                <div className='d-flex align-items-center gap-3' style={{ flexWrap: 'wrap' }}>
                  <Typography variant="h5">Available Certificates List</Typography>
                  <span style={{ fontSize: 13, color: '#888', fontFamily: 'Karla' }}>
                    {hasActiveFilters ? `${totalCount} of ${summary.total} shown` : `${summary.total} total`}
                  </span>
                  <div className="cert-legend">
                    <div className="cert-legend-item">
                      <div className="cert-legend-dot" style={{ backgroundColor: '#FF0000' }} />
                      Expired members
                    </div>
                    <div className="cert-legend-item">
                      <div className="cert-legend-dot" style={{ backgroundColor: '#F8BD00' }} />
                      Expiring soon
                    </div>
                    <div className="cert-legend-item">
                      <div className="cert-legend-dot" style={{ backgroundColor: '#e0e0e0' }} />
                      Up to date
                    </div>
                  </div>
                </div>
                <div className='d-flex gap-2 align-items-center'>
                  <Tooltip title="List view">
                    <IconButton onClick={() => setViewMode('list')} color={viewMode === 'list' ? 'error' : 'default'}>
                      <ViewListIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Grid view">
                    <IconButton onClick={() => setViewMode('grid')} color={viewMode === 'grid' ? 'error' : 'default'}>
                      <GridViewIcon />
                    </IconButton>
                  </Tooltip>
                  <Link to="/firehouse/certificates/all">
                    <ButtonTheme type='submit' variant="filled" color='primary'>View All Certificate</ButtonTheme>
                  </Link>
                  <ButtonTheme type='submit' variant="filled" color='primary' onClick={() => setAddModal(true)}>
                    Create New Certificate
                  </ButtonTheme>
                </div>
              </div>

              {/* ── GRID VIEW ── */}
              {viewMode === 'grid' ? (
                <div className="cert-cards-grid">
                  {certificates.map((cert) => (
                    <div key={cert.Certificate_id}>
                      <div className="cert-card-redesign" style={{ ...getUrgencyStyle(cert) }}>
                        {/* Card top: name + actions */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                          <div className='title' style={{ flex: 1 }}>{cert.Name}</div>
                          <div className="cert-card-actions">
                            <Tooltip title="View">
                              <img src='/images/eye.svg' style={{ cursor: 'pointer' }} alt="View"
                                onClick={() => { setViewId(cert.Certificate_id); setViewModal(true); }} />
                            </Tooltip>
                            <Tooltip title="Edit">
                              <img src='/images/edit.svg' style={{ cursor: 'pointer' }} alt="Edit"
                                onClick={() => { setAddModal(true); setViewId(cert.Certificate_id); }} />
                            </Tooltip>
                            <Tooltip title="Delete">
                              <img src='/images/delete.svg' style={{ cursor: 'pointer' }} alt="Delete"
                                onClick={() => { setDeleteModal(true); setDeleteId(cert.Certificate_id); }} />
                            </Tooltip>
                            <Tooltip title="View Members">
                              <Link to={`/firehouse/certificate-members/${cert.Certificate_id}`}>
                                <img src='/images/users.png' style={{ width: 20, height: 20, cursor: 'pointer' }} alt="Members" />
                              </Link>
                            </Tooltip>
                          </div>
                        </div>

                        {/* Count pills */}
                        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                          <Tooltip title="Members holding this certificate">
                            <Link to={`/firehouse/certificate-members/${cert.Certificate_id}`} style={{ textDecoration: 'none' }}>
                              <span className="cert-count-pill">
                                <img src='/images/users.png' style={{ width: 14, height: 14 }} alt="" />
                                {cert.certificate_employees_count}
                              </span>
                            </Link>
                          </Tooltip>
                          <Tooltip title="Positions requiring this certificate">
                            <span className="cert-count-pill">
                              <img src='/images/loc.png' style={{ width: 14, height: 14 }} alt="" />
                              {cert.certificate_positions_count}
                            </span>
                          </Tooltip>
                        </div>

                        {/* Description */}
                        {cert.Description && (
                          <p className={`desc ${cert.Description.length > 144 ? 'line-clamp-3' : ''}`}>
                            {cert.Description}
                          </p>
                        )}
                        {cert.Description?.length > 144 && (
                          <p className="see-more-text" style={{ cursor: 'pointer', fontSize: 13 }}
                            onClick={() => { setViewId(cert.Certificate_id); setViewModal(true); }}>
                            See More
                          </p>
                        )}

                        {/* Expiry stats — pinned to bottom */}
                        <div className="cert-card-expiry-section">
                          <div style={{ height: '1px', backgroundColor: 'rgba(204,204,204,0.6)', marginBottom: 8 }} />
                          <div className="cert-expiry-row">
                            <span className="cert-expiry-label">Has Expired Members</span>
                            <ExpiryBadge count={cert.expired_count} type="expired" />
                          </div>
                          <div className="cert-expiry-row">
                            <span className="cert-expiry-label">Has Expiring Soon (30 days)</span>
                            <ExpiryBadge count={cert.expiring_30_days_count} type="expiring" />
                          </div>
                          <div className="cert-expiry-row">
                            <span className="cert-expiry-label">Has Expiring This Month</span>
                            <ExpiryBadge count={cert.expiring_this_month_count} type="expiring" />
                          </div>
                          <div className="cert-expiry-row">
                            <span className="cert-expiry-label">Has Expiring This Year</span>
                            <ExpiryBadge count={cert.expiring_this_year_count} type="expiring" />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                /* ── LIST VIEW ── */
                <TableContainer sx={{ mt: 2 }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>Members</TableCell>
                        <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>Positions</TableCell>
                        <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>Description</TableCell>
                        <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', color: '#cc0000' }}>Expired</TableCell>
                        <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', color: '#cc7700' }}>Exp. Soon</TableCell>
                        <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', color: '#cc7700' }}>Exp. Month</TableCell>
                        <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', color: '#cc7700' }}>Exp. Year</TableCell>
                        <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }} align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {certificates.map((cert, idx) => (
                        <TableRow
                          key={cert.Certificate_id}
                          className="cert-table-row"
                          sx={{
                            backgroundColor: idx % 2 === 0 ? '#ffffff' : '#fafafa',
                            borderLeft: getUrgencyStyle(cert).borderLeft,
                            '&:hover': { backgroundColor: '#f0f4ff !important' },
                          }}
                        >
                          <TableCell sx={{ fontWeight: 500 }}>{cert.Name}</TableCell>
                          <TableCell>
                            <Link to={`/firehouse/certificate-members/${cert.Certificate_id}`} style={{ color: '#BF1D2D', fontWeight: 600, textDecoration: 'none' }}>
                              {cert.certificate_employees_count}
                            </Link>
                          </TableCell>
                          <TableCell>{cert.certificate_positions_count}</TableCell>
                          <TableCell sx={{ maxWidth: 220, color: '#555', fontSize: 13 }}>
                            <span className={cert.Description?.length > 80 ? 'line-clamp-3' : ''}>
                              {cert.Description || '—'}
                            </span>
                          </TableCell>
                          <TableCell><ExpiryBadge count={cert.expired_count} type="expired" /></TableCell>
                          <TableCell><ExpiryBadge count={cert.expiring_30_days_count} type="expiring" /></TableCell>
                          <TableCell><ExpiryBadge count={cert.expiring_this_month_count} type="expiring" /></TableCell>
                          <TableCell><ExpiryBadge count={cert.expiring_this_year_count} type="expiring" /></TableCell>
                          <TableCell align="right">
                            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}>
                              <Tooltip title="View">
                                <img src='/images/eye.svg' style={{ cursor: 'pointer' }} alt="View"
                                  onClick={() => { setViewId(cert.Certificate_id); setViewModal(true); }} />
                              </Tooltip>
                              <Tooltip title="Edit">
                                <img src='/images/edit.svg' style={{ cursor: 'pointer' }} alt="Edit"
                                  onClick={() => { setAddModal(true); setViewId(cert.Certificate_id); }} />
                              </Tooltip>
                              <Tooltip title="Delete">
                                <img src='/images/delete.svg' style={{ cursor: 'pointer' }} alt="Delete"
                                  onClick={() => { setDeleteModal(true); setDeleteId(cert.Certificate_id); }} />
                              </Tooltip>
                              <Tooltip title="View Members">
                                <Link to={`/firehouse/certificate-members/${cert.Certificate_id}`}>
                                  <img src='/images/users.png' style={{ width: 18, height: 18, cursor: 'pointer' }} alt="Members" />
                                </Link>
                              </Tooltip>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* ── Pagination ── */}
              <div className='mt-4'>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  className="mt-4 d-flex"
                  style={{ justifyContent: 'end' }}
                />
              </div>
            </div>
          )}

          {/* ── Delete modal ── */}
          <Modal show={deletModal} onHide={() => setDeleteModal(false)}>
            <Modal.Header closeButton>
              <Modal.Title>Delete Certificate</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div style={{ color: '#555555' }}>
                Are you sure to delete this Certificate?
              </div>
              <Row>
                <Col md='auto'>
                  <ButtonTheme type='submit' variant="filled" color='white' className='mt-4' onClick={() => setDeleteModal(false)}>
                    Cancel
                  </ButtonTheme>
                </Col>
                <Col md='auto'>
                  <ButtonTheme type='submit' variant="filled" color='primary' className='mt-4' onClick={deleteCertificate}>
                    Delete
                  </ButtonTheme>
                </Col>
              </Row>
            </Modal.Body>
          </Modal>

          {addModal && (
            <AddCerificate addModal={addModal} setAddModal={setAddModal} editId={viewId}
              setViewId={setViewId} loadCertificates={loadCertificates} />
          )}
          {viewModal && (
            <ViewCertificate id={viewId} viewModal={viewModal} setViewModal={setViewModal}
              setViewId={setViewId} />
          )}
        </Paper>
      )}
    </>
  );
};

export default Certificates;
